import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { format } from 'date-fns';
import { Check } from 'lucide-react-native';
import { useCallback, type ComponentProps } from 'react';
import { Text as NativeText, StyleSheet, type TextStyle } from 'react-native';
import { twJoin } from 'tailwind-merge';

import {
  type CustomMessageTextProps,
  type ICustomMessage,
  StyledDay,
  StyledMessageText,
} from '@/components/chat';
import { useInlineCitations } from '@/components/chat/bubbles/references/InlineCitationChip';
import type { InlineCitation } from '@/components/chat/bubbles/references/inlineCitations';
import {
  renderMessageTextPieces,
  splitMessageRun,
} from '@/components/chat/messageText/messageTextPieces';
import { Box } from '@/components/ui/box';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { isRecipientContent, useMessageLinkOpener } from '@/hooks/chat/useMessageLinkOpener';
import type { ClinicalCase } from '@/types/clinicalCase';
import { MESSAGE_URL_PATTERN } from '@/util/messageLinks';

export function renderDay(props: ComponentProps<typeof StyledDay>) {
  return (
    <StyledDay
      {...props}
      containerClassName="m-0 py-0 pb-1"
      textClassName="text-xs text-typography-400 p-0 m-0"
    />
  );
}

function isSameClinicalCase(current: ClinicalCase | undefined, next: ClinicalCase | undefined) {
  if (current?.status !== next?.status) {
    return false;
  }

  return (
    (current?.status === 'written' ? current.line : null) ===
    (next?.status === 'written' ? next.line : null)
  );
}

export function shouldUpdateMessageFiles(
  props: { currentMessage: unknown },
  nextProps: { currentMessage: unknown }
) {
  if (
    !isSameClinicalCase(
      (props.currentMessage as ICustomMessage)?.clinicalCase,
      (nextProps.currentMessage as ICustomMessage)?.clinicalCase
    )
  ) {
    return true;
  }

  const currentFiles = (props.currentMessage as ICustomMessage)?.files;

  const nextFiles = (nextProps.currentMessage as ICustomMessage)?.files;

  if (currentFiles?.length !== nextFiles?.length) {
    return true;
  }

  const curRole = (props.currentMessage as ICustomMessage)?.contentRole;

  const nextRole = (nextProps.currentMessage as ICustomMessage)?.contentRole;

  if (curRole !== nextRole) {
    return true;
  }

  if (!currentFiles || !nextFiles) {
    return false;
  }

  const currentKeys = currentFiles.map((f) => f.s3Key).join(',');

  const nextKeys = nextFiles.map((f) => f.s3Key).join(',');

  return currentKeys !== nextKeys;
}

/**
 * Message text with its links wired up. GiftedChat runs `parsePatterns` ahead
 * of its own, so this pattern claims URLs before the default one, which cuts a
 * link off at the first parenthesis. Phone numbers and emails still fall
 * through to GiftedChat's defaults.
 *
 * A component rather than inline in the renderer, because GiftedChat calls the
 * renderer as a plain function and the link handler needs a hook.
 */
function LinkedMessageText({
  conversationId,
  recipientId,
  ...props
}: ComponentProps<typeof StyledMessageText> & {
  conversationId: string | undefined;
  recipientId: number | undefined;
}) {
  const message = props.currentMessage as ICustomMessage | undefined;

  const openLink = useMessageLinkOpener({
    conversationId,
    messageSid: message?.messageId,
    isReference: isRecipientContent(message, recipientId),
  });

  const parseMessageLinks = useCallback(
    (linkStyle: TextStyle) =>
      // GiftedChat types this return as an empty tuple, so any pattern needs a cast.
      [{ pattern: MESSAGE_URL_PATTERN, style: linkStyle, onPress: openLink }] as unknown as [],
    [openLink]
  );

  const inlineCitations = useInlineCitations();

  if (inlineCitations) {
    return (
      <CitedMessageText
        {...props}
        citations={inlineCitations.citations}
        onOpenCitation={inlineCitations.onOpenCitation}
        openLink={openLink}
      />
    );
  }

  return <StyledMessageText {...props} parsePatterns={parseMessageLinks} />;
}

/**
 * Message text with reference numbers placed after the words that cite them,
 * for a bubble that supplies inline citations. GiftedChat's text parses a
 * plain string and cannot hold a chip, so this draws the same container and
 * type itself, through the renderer statement answers use, with links,
 * phone numbers and email addresses opened through the same handler and
 * analytics. Cited words open the reference like their chip, except a cited
 * link, which still opens the link: its chip sits right beside it.
 *
 * The outer text is React Native's own, as GiftedChat's is: it turns
 * `selectable` into a text selection style on web, where the Gluestack text
 * passes the prop to the DOM as an invalid attribute.
 */
function CitedMessageText({
  citations,
  onOpenCitation,
  openLink,
  currentMessage,
  position = 'left',
  textStyle,
  linkStyle,
  textProps,
  rightContainerClassName,
  leftContainerClassName,
  customTextClassName,
}: ComponentProps<typeof StyledMessageText> & {
  citations: readonly InlineCitation[];
  onOpenCitation: (number: number) => void;
  openLink: (url: string) => void;
}) {
  const body = currentMessage?.text ?? '';

  const sideTextStyle = StyleSheet.flatten([GIFTED_TEXT_STYLE, textStyle?.[position]]);

  const sideLinkStyle = StyleSheet.flatten([
    { color: sideTextStyle.color, textDecorationLine: 'underline' as const },
    linkStyle?.[position],
  ]);

  return (
    <Box className={position === 'right' ? rightContainerClassName : leftContainerClassName}>
      <NativeText
        {...textProps}
        testID="message-text"
        className={customTextClassName}
        style={sideTextStyle}>
        {renderMessageTextPieces(splitMessageRun(body, 0, citations), 'p', {
          position,
          linkStyle: sideLinkStyle,
          onOpenLink: openLink,
          onOpenCitation,
        })}
      </NativeText>
    </Box>
  );
}

/** GiftedChat's own message text size and line height, which the chat's
 * text style builds on rather than restating. */
const GIFTED_TEXT_STYLE: TextStyle = { fontSize: 16, lineHeight: 20 };

export function createRenderMessageText({
  outgoingTextColor,
  incomingTextColor,
  conversationId,
  recipientId,
}: {
  outgoingTextColor: string;
  incomingTextColor: string;
  conversationId: string | undefined;
  /** The inquiry recipient, whose links report as references when opened. */
  recipientId: number | undefined;
}) {
  return (props: CustomMessageTextProps) => {
    const currentMsg = props.currentMessage as ICustomMessage | undefined;

    if (currentMsg?.type === 'reference') {
      return null;
    }

    const textTopShell = twJoin(
      'max-w-full min-w-0 rounded-t-2xl px-3 pt-3',
      isWeb && 'break-words'
    );

    return (
      <LinkedMessageText
        {...props}
        conversationId={conversationId}
        recipientId={recipientId}
        textProps={{ selectable: true }}
        rightContainerClassName={twJoin('bg-primary-500', textTopShell)}
        leftContainerClassName={twJoin('bg-transparent', textTopShell)}
        customTextClassName="m-0"
        linkStyle={{
          right: {
            color: outgoingTextColor,
          },
          left: {
            color: incomingTextColor,
          },
        }}
        textStyle={{
          right: {
            color: outgoingTextColor,
            marginLeft: 0,
            marginRight: 0,
            marginBottom: 4,
            marginTop: 0,
            fontFamily: 'Inter-Regular',
            fontWeight: '400',
            fontSize: 16,
            flexShrink: 1,
            flexWrap: 'wrap',
            ...(isWeb ? { overflowWrap: 'break-word' as const } : {}),
          },
          left: {
            color: incomingTextColor,
            marginLeft: 0,
            marginRight: 0,
            marginBottom: 4,
            marginTop: 0,
            fontFamily: 'Inter-Regular',
            fontWeight: '400',
            fontSize: 16,
            flexShrink: 1,
            flexWrap: 'wrap',
            ...(isWeb ? { overflowWrap: 'break-word' as const } : {}),
          },
        }}
      />
    );
  };
}

interface BubbleTimestampProps {
  /** Message providing `createdAt`, `received`, `sent`. Accepts the loose
   * GiftedChat IMessage shape so callers can forward `currentMessage`
   * without re-typing. */
  currentMessage: { createdAt: Date | number; received?: boolean; sent?: boolean };
  /** Bubble side — drives muted-vs-light typography. */
  position: 'left' | 'right';
}

/**
 * Timestamp + delivery-tick row shown at the bottom of a chat bubble.
 *
 * Extracted from `createRenderTime` so bubble variants that already own
 * their own bottom row (e.g. the statement-references footer) can render
 * the timestamp inline with their last row, instead of letting GiftedChat
 * stack a separate time row underneath. Plain text bubbles keep going
 * through `createRenderTime`, which now delegates to this component.
 */
export function BubbleTimestamp({ currentMessage, position }: BubbleTimestampProps) {
  const { createdAt, received, sent } = currentMessage;

  const textColorClassName = position === 'left' ? 'text-typography-400' : 'text-typography-0';

  return (
    <HStack space="xs" className="items-center">
      <Text size="2xs" className={textColorClassName}>
        {format(createdAt, 'p')}
      </Text>

      <HStack>
        {[received, sent].filter(Boolean).map((_, index) => (
          <Icon size="xs" key={index} as={Check} className={textColorClassName} />
        ))}
      </HStack>
    </HStack>
  );
}

export function createRenderTime() {
  return ({ currentMessage, position }: { currentMessage?: any; position?: 'left' | 'right' }) => {
    if (!currentMessage || !position) {
      return null;
    }

    return <BubbleTimestamp currentMessage={currentMessage} position={position} />;
  };
}
